import { RecordOptionPath } from 'app/pages/record/const';
import { ModalRecordHome } from 'app/pages/record/recordModal/ModalRecordHome';
import { ModalRecordOption } from 'app/pages/record/recordModal/ModalRecordOption';
import React, { useState } from 'react';
import {
  GetSignedUrlsParams,
  getVoiceoverSignedURLs,
  uploadVoiceoverAudio,
} from 'lib/api';
import { generateTempFileName } from 'app/pages/video/videoVoiceover/components/util';
import { useToastError } from 'lib/hooks';
import { ModalHeader } from 'app/pages/guides/modals/VisibilityModal';
import { HeaderSide } from 'app/pages/library/videos/videoList/components/SendAndShare';
import { ChooseRecordingOptions } from 'app/pages/recordv1/ChooseRecordingOptions';
import { LoadingIndicator, NewModal } from 'lib/components';
import { Button } from 'react-covideo-common';
import { MdClose } from 'react-icons/md';
import { useVoiceOverSignUrlWithAudioUpload } from './useVoiceOverSignUrlWithAudioUpload';
import { RecordSettingsProvider } from 'lib/context/record/provider';

type Props = {
  handleAddExternalVideo: (uploadedUrl: string, previewUrl: string) => void;
  newFlowRecordingMerge: boolean;
  onModalCloseHandler: () => void;
};

export const AddRecordingToMerge = ({
  handleAddExternalVideo,
  newFlowRecordingMerge,
  onModalCloseHandler,
}: Props) => {
  const { showError } = useToastError();

  const { startUpoladingVoiceover, isLoading } =
    useVoiceOverSignUrlWithAudioUpload();
  const [showRecordOption, setShowRecordOption] =
    useState<RecordOptionPath | null>(null);

  const [uploading, setUploading] = useState(false);

  // Uploads temp video file to self-deleting voiceover bucket
  const handleRecordingBlobReady = async (url: string) => {
    setUploading(true);
    setShowRecordOption(null);
    const blob = await fetch(url || '').then(r => r.blob());
    const tempFileName = generateTempFileName('merge_recording', 'webm');
    const file = new File([blob], tempFileName);

    const signedUrlData: GetSignedUrlsParams = {
      fileName: file.name,
      mimeType: 'video/webm',
      folder: '',
    };
    const signedURL = await getVoiceoverSignedURLs(signedUrlData);
    if (!signedURL || signedURL instanceof Error) {
      setUploading(false);
      showError(signedURL);
      return;
    }

    const data = {
      file: file as File,
      uploadURL: signedURL.uploadUrl,
      mimeType: signedUrlData.mimeType,
    };
    try {
      await uploadVoiceoverAudio({ data });
    } catch (error) {
      setUploading(false);
      showError(error);
      return;
    }
    setUploading(false);
    handleAddExternalVideo(signedURL.downloadUrl, url);
  };

  const onStartUpladingVoiceOverHandler = async (blob: Blob) => {
    try {
      const response = await startUpoladingVoiceover(blob);
      handleAddExternalVideo(response.downloadUrl, response.url);
    } catch (error) {
      showError(error);
      onModalCloseHandler();
    }
  };

  return (
    <>
      {newFlowRecordingMerge ? (
        <NewModal
          hideHeader
          headerText=''
          closeModal={onModalCloseHandler}
          style={{ content: { width: '100%', height: '100vh' } }}
        >
          <>
            {isLoading ? (
              <div>
                <LoadingIndicator isLoading={true} />
              </div>
            ) : (
              <>
                <ModalHeader>
                  <HeaderSide>
                    <Button
                      text='Close'
                      icon={<MdClose size='18' />}
                      onClick={onModalCloseHandler}
                      variant='transparent'
                    />
                  </HeaderSide>
                </ModalHeader>
                <RecordSettingsProvider>
                  <ChooseRecordingOptions
                    showLegacyRecorderSwitch={false}
                    showHelpDesk={false}
                    showPreview={false}
                    onStartUpladingVoiceOverHandler={
                      onStartUpladingVoiceOverHandler
                    }
                    extendStyles={{
                      showRecorderWrapper: {
                        padding: 32,
                      },
                    }}
                  />
                </RecordSettingsProvider>
              </>
            )}
          </>
        </NewModal>
      ) : (
        <>
          <ModalRecordHome
            handleModalClose={onModalCloseHandler}
            onRecordOptionClick={(recordOption: RecordOptionPath | null) => {
              setShowRecordOption(recordOption);
            }}
            loading={uploading}
          />
          {showRecordOption && (
            <ModalRecordOption
              handleModalClose={() => {
                onModalCloseHandler();
                setShowRecordOption(null);
              }}
              handleShowRecordHome={() => {
                onModalCloseHandler();
                setShowRecordOption(null);
              }}
              handleRecordingBlobReady={handleRecordingBlobReady}
              recordOption={showRecordOption}
            />
          )}
        </>
      )}
    </>
  );
};
